import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Scissors,
  Tag,
  Layers,
  Sparkles,
  CircleCheck,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile + 2 engagements ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison directe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos cartons à domicile à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Oubliez les ruptures de stock dans les magasins de bricolage
            et l&apos;effort fastidieux consistant à faire entrer des
            liasses d&apos;emballages poussiéreuses dans votre coffre. Nous
            gérons l&apos;acheminement complet de vos modules directement
            jusqu&apos;à votre porte à {city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Filière logistique privée",
              body: "Nous n'utilisons aucun réseau de messagerie impersonnel. C'est notre propre structure francilienne qui charge et dépose vos fournitures. Vous profitez d'un interlocuteur unique, d'une ponctualité rigoureuse et d'un respect total de vos espaces.",
              Icon: Truck,
            },
            {
              title: "Garantie de rigidité nominale",
              body: "Voyageant à l'abri de l'humidité et de la poussière dans nos véhicules utilitaires hermétiques, vos cartons conservent leurs propriétés techniques d'origine. Ils arrivent intacts, sains et immédiatement prêts à être façonnés.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Storytelling pistolet à scotch ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
              L&apos;outil qui change tout
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Découvrez pourquoi le dévidoir de scotch pro va sauver vos nuits
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Quiconque a déjà préparé un déménagement connaît la
              frustration de chercher l&apos;extrémité du ruban adhésif avec
              l&apos;ongle à deux heures du matin, dans le silence d&apos;un
              logement à moitié vide. C&apos;est précisément pour vous
              éviter cette perte d&apos;énergie que le pistolet dévidoir est
              indispensable.
            </p>

            <div className="mt-6 space-y-3">
              {[
                {
                  title: "Une application fluide en une seule main",
                  body: "Cet outil de calibre industriel applique, tend et coupe l'adhésif d'un seul mouvement naturel. Pendant qu'une main maintient les rabats du carton fermés, l'autre applique le ruban en trois secondes chrono.",
                },
                {
                  title: "Une réduction drastique de la fatigue",
                  body: "Plus besoin de ciseaux, plus de ruban qui s'enroule sur lui-même ou qui se déchire de biais. En automatisant le geste, vous divisez par trois le temps de fermeture de vos colis. Multiplié par 40 ou 50 cartons, ce sont littéralement deux heures de sommeil et un calme précieux de gagnés sur votre planning.",
                },
              ].map(({ title, body }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
                >
                  <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#FF7A00] text-white shrink-0">
                    <CircleCheck className="h-4 w-4" strokeWidth={2.6} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — visual CTA card */}
          <div className="lg:col-span-5">
            <div className="bg-[#0f1d2b] text-white rounded-3xl p-7 md:p-8 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#FF7A00]/20 blur-2xl" />
              <div className="relative">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-[#FF7A00] mb-5">
                  <Scissors className="h-8 w-8 text-white" strokeWidth={2.4} />
                </div>
                <h3 className="font-display font-extrabold text-xl md:text-2xl">
                  Le pistolet à scotch pro
                </h3>
                <p className="mt-3 text-[14px] text-white/70 leading-relaxed">
                  L&apos;outil indispensable des déménageurs : application,
                  tension et découpe en un seul geste, sur 40+ cartons sans
                  effort.
                </p>
                <ul className="mt-5 space-y-2.5 text-[13.5px] text-white/85">
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[#FF7A00]" strokeWidth={2.6} />
                    3× plus rapide qu&apos;à la main
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[#FF7A00]" strokeWidth={2.6} />
                    Coupe nette et propre
                  </li>
                  <li className="flex items-center gap-2">
                    <CircleCheck className="h-4 w-4 text-[#FF7A00]" strokeWidth={2.6} />
                    Conçu pour usage intensif
                  </li>
                </ul>
                <Link
                  to="/produit/devidoir-pistolet-adhesif"
                  className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#FF7A00] hover:bg-[#E66E00] text-white font-bold text-[14px] transition-colors"
                  data-testid="city-rueil-malmaison-cta-pistolet"
                >
                  Voir le pistolet à scotch
                  <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Verrouillage hermétique : 3 accessoires ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Accessoires de scellage
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Verrouillage hermétique : notre collection d&apos;accessoires de
            scellage et leur rôle
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un contenant de qualité supérieure n&apos;offre une sécurité
            totale que s&apos;il est associé à un système de fermeture
            adapté. Découvrez nos solutions techniques pour blinder vos
            colis.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Le ruban adhésif acrylique à forte cohésion",
              body: "Formulé pour fusionner instantanément avec les fibres du carton brut, il garantit que la base de vos boîtes ne cédera pas, même sous la pression de vos charges les plus denses.",
              Icon: Tag,
              to: "/produit/rouleau-adhesif",
              label: "Voir le rouleau",
            },
            {
              title: "Le film étirable de liaison et d'enveloppement",
              body: "Idéal pour solidariser plusieurs colis légers entre eux, maintenir les tiroirs de vos petits meubles immobiles ou protéger vos cartons de la pluie et des frottements pendant le portage extérieur.",
              Icon: Layers,
              to: "/produit/film-etirable",
              label: "Voir le film étirable",
            },
            {
              title: "Les étiquettes d'identification pré-imprimées",
              body: "Indispensables pour structurer votre déchargement. Elles scellent vos informations clés sur le flanc des boîtes (pièce de destination, niveau de fragilité), permettant aux équipes de dispatcher le matériel instantanément à l'arrivée.",
              Icon: Sparkles,
              to: "/fermeture-accessoires",
              label: "Voir les accessoires",
            },
          ].map(({ title, body, Icon, to, label }) => (
            <Link
              key={title}
              to={to}
              className="block bg-white rounded-2xl border border-[#cfdbe6] hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 p-5 group"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[14.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] group-hover:gap-2.5 transition-all">
                {label}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-rueil-malmaison-cta-fermeture"
          >
            Voir tous les accessoires de fermeture
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-rueil-malmaison-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
